import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'

export default function About() {
  return (
    <>
      <SEOHead
        title="Om Agstner Byg - Agstner Byg ApS"
        description="Læs om Agstner Byg ApS i Vissenbjerg. Vi tilbyder solidt tømrerarbejde, tag og renovering med fokus på kvalitet, personlig service og tæt dialog."
      />

      {/* Hero */}
      <section className="relative py-24 bg-zinc-950 overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00027.jpeg"
            alt="Om Agstner Byg ApS"
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/85 via-zinc-950/50 to-zinc-950/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <div className="flex items-center gap-2 text-orange-500 font-display text-xs uppercase tracking-widest">
              <span className="w-6 h-0.5 bg-orange-500"></span>
              <span>OM VIRKSOMHEDEN</span>
            </div>

            <h1 className="text-4xl sm:text-6xl font-display font-black text-white uppercase tracking-tight">
              Om Agstner Byg ApS
            </h1>

            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
              Kvalitetsbevidst håndværksfirma i Vissenbjerg med fokus på faglig stolthed, personlig service og direkte kontakt.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="px-8 py-4 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest transition-all border border-orange-500"
              >
                Få et tilbud
              </Link>

              <a
                href="tel:+4552502543"
                className="px-6 py-4 rounded-none bg-zinc-900 hover:bg-zinc-800 text-orange-500 font-display font-bold text-xs uppercase tracking-widest border border-zinc-800 transition-all whitespace-nowrap"
              >
                <span className="whitespace-nowrap">Ring på 52 50 25 43</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main About Story */}
      <section className="py-20 bg-zinc-900 border-b border-zinc-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-2xl sm:text-4xl font-display font-bold text-white uppercase tracking-tight">
                Solidt håndværk og gennemtænkte løsninger
              </h2>

              <div className="prose prose-invert max-w-none text-zinc-300 text-sm leading-relaxed space-y-4">
                <p>
                  Agstner Byg ApS er en engageret og kvalitetsbevidst tømrervirksomhed beliggende på Odensevej 36 i Vissenbjerg (CVR: 44000997). Vi lægger vægt på godt håndværk, tæt dialog med kunden og løsninger, der holder i mange år fremover.
                </p>
                <p>
                  Uanset om opgaven drejer sig om nyt tag, en større renovering eller løbende tømrerarbejde, går vi til opgaven med samme omhyggelighed og præcision.
                </p>
              </div>

              {/* Company Info Box (CAD Wireframe style) */}
              <div className="p-6 bg-zinc-950 border border-zinc-800 space-y-3">
                <h3 className="text-xs font-display font-bold text-orange-500 uppercase tracking-widest">
                  Virksomhedsoplysninger
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-zinc-300">
                  <div>
                    <span className="text-[10px] text-zinc-500 font-mono block uppercase">Virksomhedsnavn</span>
                    <span className="font-bold text-white uppercase font-display">Agstner Byg ApS</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-500 font-mono block uppercase">CVR-nummer</span>
                    <span className="font-bold text-white font-mono">44000997</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-500 font-mono block uppercase">Adresse</span>
                    <span className="font-bold text-white">Odensevej 36, 5492 Vissenbjerg</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-500 font-mono block uppercase">Telefon</span>
                    <a href="tel:+4552502543" className="font-bold text-orange-500 hover:underline whitespace-nowrap">
                      52 50 25 43
                    </a>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-500 font-mono block uppercase">Facebook</span>
                    <a
                      href="https://www.facebook.com/profile.php?id=61590981091175"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-orange-500 hover:underline whitespace-nowrap inline-flex items-center gap-1.5"
                    >
                      <i className="fa-brands fa-facebook" aria-hidden="true"></i>
                      <span>Følg os på Facebook</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-zinc-800 bg-zinc-950 p-0">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00063.jpeg"
                  alt="Agstner Byg ApS håndværk"
                  className="w-full h-[450px] object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Values & Principles */}
      <section className="py-20 bg-zinc-950 text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-display font-bold uppercase tracking-widest text-orange-500">KVALITET I ARBEJDET</span>
            <h2 className="text-2xl sm:text-4xl font-display font-bold text-white uppercase mt-1">
              Vores tilgang til opgaven
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-zinc-900 p-8 border border-zinc-800 space-y-4">
              <div className="w-10 h-10 bg-orange-500 text-zinc-950 flex items-center justify-center font-bold text-lg">
                <i className="fa-solid fa-ruler-combined" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-display font-bold text-white uppercase">Præcision og faglighed</h3>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Vi lægger vægt på nøjagtige måltagninger og korrekt udførelse efter gældende håndværksmæssige principper.
              </p>
            </div>

            <div className="bg-zinc-900 p-8 border border-zinc-800 space-y-4">
              <div className="w-10 h-10 bg-orange-500 text-zinc-950 flex items-center justify-center font-bold text-lg">
                <i className="fa-solid fa-handshake" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-display font-bold text-white uppercase">Personlig kontakt</h3>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Du taler direkte med os om din opgave. Vi vægter den tætte dialog og gensidige aftaler højt.
              </p>
            </div>

            <div className="bg-zinc-900 p-8 border border-zinc-800 space-y-4">
              <div className="w-10 h-10 bg-orange-500 text-zinc-950 flex items-center justify-center font-bold text-lg">
                <i className="fa-solid fa-house-circle-check" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-display font-bold text-white uppercase">Holdbare løsninger</h3>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Vi bygger til at holde i mange år. Gennemarbejdede løsninger giver den bedste oplevelse for kunden.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
